import { PlusOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Input, PageHeader, Radio, Select, Space, Table, Tag, Tooltip } from 'antd';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import moment from 'moment';
import { INVESTORS } from './apollo_queries';
import { TableMainLabel } from '../components/styled';
import NewAdminModal from '../components/NewAdminModal';

// Reconnected to constlending-api-next `adminUsers` (paginated, PII-safe pilot). The financial-total
// columns + `individualAccount` are DEFERRED (full users-admin migration), so this is a reduced table.
// The whole row opens the user (TL, 2026-09-09) — there is no Action column.
// The role, the investment filter, the search and the page live in the URL (`?role=&state=&q=&p=`),
// like Properties (TL, 2026-09-10).

// Investors vs operators — the split `type` could never make: in prod 3.561 of 3.562 users are `investor`,
// so the old Type column said nothing. There is no "All" on purpose (TL, 2026-09-10): the two audiences
// are read separately, and `total` follows the tab.
const ROLE_INVESTOR = 'investor';
const ROLE_ADMIN = 'admin';
const ROLE = {
  [ROLE_INVESTOR]: { label: 'Investors', noun: 'investors' },
  [ROLE_ADMIN]: { label: 'Admins', noun: 'admins' },
};

// `kycStatus` is the identity provider's own vocabulary, stored untranslated and made readable only here.
// A value outside this table shows as-is rather than under a label that would be a guess.
const KYC_TAG = {
  passed: { color: 'green', label: 'Passed' },
  failed: { color: 'red', label: 'Failed' },
  review: { color: 'orange', label: 'In review' },
  pending: { color: 'orange', label: 'Pending' },
  documents_required: { color: 'orange', label: 'Documents required' },
  documents_received: { color: 'orange', label: 'Documents received' },
  none: { color: 'default', label: 'Not started' },
};

// `null` is not a status: the person never opened an individual account (2.183 of 3.562 in prod).
const kycCell = (kycStatus) => {
  if (!kycStatus) {
    return <Tooltip title="No individual account">—</Tooltip>;
  }
  const tag = KYC_TAG[kycStatus] || { color: 'default', label: kycStatus };
  return <Tag color={tag.color}>{tag.label}</Tag>;
};

// How many commitments were never called off. Split from the state tags into its own column (TL,
// 2026-09-10): stacked in one cell the number and the tags read as one blob.
const investmentCountCell = (_, { investmentCount }) =>
  investmentCount ? <TableMainLabel>{investmentCount}</TableMainLabel> : '—';

// The state of those commitments. The count comes out of the same batched read as the two flags, so
// `0` and "No investments" can never disagree.
const investmentStatusCell = (_, { hasPendingInvestment, hasCompletedInvestment }) =>
  hasPendingInvestment || hasCompletedInvestment ? (
    <>
      {hasPendingInvestment && <Tag color="orange">Pending</Tag>}
      {hasCompletedInvestment && <Tag color="green">Completed</Tag>}
    </>
  ) : (
    <Tag color="default">No investments</Tag>
  );

const nameColumn = {
  title: 'Name',
  render: ({ firstName, lastName }) =>
    firstName || lastName ? (
      <TableMainLabel>{`${firstName || ''} ${lastName || ''}`.trim()}</TableMainLabel>
    ) : (
      <TableMainLabel style={{ color: '#c0392b' }}>—</TableMainLabel>
    ),
};

const emailColumn = { title: 'Email', dataIndex: 'email', sorter: true, showSorterTooltip: false };

const kycColumn = { title: 'KYC', dataIndex: 'kycStatus', render: kycCell };

const investmentCountColumn = {
  title: 'Investments',
  align: 'right',
  width: 110,
  render: investmentCountCell,
};

const investmentStatusColumn = { title: 'Status', render: investmentStatusCell };

const registeredColumn = {
  title: 'Registered',
  dataIndex: 'createdAt',
  sorter: true,
  showSorterTooltip: false,
  render: (t) =>
    t ? <Tooltip title={moment(t).format('LLL')}>{moment(t).fromNow()}</Tooltip> : '—',
};

const chevronColumn = {
  width: 40,
  render: () => <RightOutlined style={{ color: '#bfbfbf' }} />,
};

// An operator does not invest and has no individual account, so on Admins those three columns would be
// three columns of dashes.
const columnsFor = (role) =>
  role === ROLE_ADMIN
    ? [nameColumn, emailColumn, registeredColumn, chevronColumn]
    : [
        nameColumn,
        emailColumn,
        kycColumn,
        investmentCountColumn,
        investmentStatusColumn,
        registeredColumn,
        chevronColumn,
      ];

// antd sorter column field -> adminUsers `AdminUserSortField` enum value.
const SORT_FIELD = { email: 'Email', createdAt: 'CreatedAt' };

// `?state=` value -> label; the key IS the adminUsers `AdminUserInvestmentState` enum value.
const INVESTMENT_STATE = {
  pending: 'With a pending investment',
  completed: 'With a completed investment',
  none: 'No investments',
};

export function Investors() {
  const history = useHistory();
  const location = useLocation();
  const params = qs.parse(location.search);
  const page = parseInt(params.p, 10) || 1;
  const state = INVESTMENT_STATE[params.state] ? params.state : undefined;
  const role = ROLE[params.role] ? params.role : ROLE_INVESTOR;
  const query = (params.q || '').trim();
  // What is TYPED; `query` is what the search button committed to the URL.
  const [draft, setDraft] = useState(query);
  const [creating, setCreating] = useState(false);
  const [pageCount, setPageCount] = useState(50);
  const [sortBy, setSortBy] = useState('CreatedAt');
  const [sortDirection, setSortDirection] = useState('DESC');

  const navigate = (next) =>
    history.push(`?${qs.stringify({ role, state, q: query || undefined, p: page, ...next })}`);

  // Admins do not invest, so the investment filter neither shows nor travels on that tab: left in the
  // URL from the other tab it would ask for admins with a pending investment and answer nothing.
  const isAdminTab = role === ROLE_ADMIN;

  const { data, loading, error, refetch } = useQuery(INVESTORS, {
    variables: {
      count: pageCount,
      q: query,
      page,
      sortBy,
      sortDirection,
      investmentState: isAdminTab ? undefined : state,
      role,
    },
  });

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const items = get(data, 'adminUsers.items', []);
  const total = get(data, 'adminUsers.total', 0);

  const onSearch = (value) => navigate({ q: value.trim() || undefined, p: 1 });

  const onChange = (pagination, _, sorter) => {
    if (pagination.current !== page) {
      navigate({ p: pagination.current });
    }
    if (sorter && sorter.field) {
      const mapped = SORT_FIELD[sorter.field] || 'CreatedAt';
      if (mapped !== sortBy) setSortBy(mapped);
      if (sorter.order) {
        setSortDirection(sorter.order === 'ascend' ? 'ASC' : 'DESC');
      }
    }
    if (pageCount !== pagination.pageSize) {
      setPageCount(pagination.pageSize);
    }
  };

  return (
    <>
      <PageHeader
        title="Users"
        subTitle={loading ? null : `${total} ${ROLE[role].noun}`}
        style={{ padding: '0 0 16px' }}
        extra={
          <Space>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={role}
              onChange={(e) => navigate({ role: e.target.value, p: 1 })}
              options={Object.keys(ROLE).map((value) => ({ value, label: ROLE[value].label }))}
            />
            {isAdminTab ? (
              // Creating an admin is the one WRITE on this screen (TL, 2026-09-12), and it lives on the
              // tab that lists them: on Investors it would offer to create the other audience.
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreating(true)}
              >
                New admin
              </Button>
            ) : (
              <Select
                style={{ width: 240 }}
                value={state || 'all'}
                onChange={(value) => navigate({ state: value === 'all' ? undefined : value, p: 1 })}
              >
                <Select.Option value="all">All</Select.Option>
                {Object.keys(INVESTMENT_STATE).map((value) => (
                  <Select.Option key={value} value={value}>
                    {INVESTMENT_STATE[value]}
                  </Select.Option>
                ))}
              </Select>
            )}
            <Input.Search
              placeholder="Search by name, email or id"
              allowClear
              enterButton
              style={{ width: 360 }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onSearch={onSearch}
            />
          </Space>
        }
      />
      <Table
        rowKey="id"
        columns={columnsFor(role)}
        dataSource={items}
        onChange={onChange}
        rowClassName="clickable-row"
        onRow={(record) => ({ onClick: () => history.push(`/users/${record.id}`) })}
        pagination={{
          total,
          pageSize: pageCount,
          position: ['bottomCenter'],
          current: page,
          hideOnSinglePage: true,
          pageSizeOptions: ['10', '20', '30', '40', '50'],
          showTotal: (count) => `${count} ${ROLE[role].noun}`,
        }}
        loading={loading}
      />
      <NewAdminModal
        visible={creating}
        onClose={() => setCreating(false)}
        onCreated={refetch}
      />
    </>
  );
}
